import { visibleNodes } from "./native-visible";
import { workspaceGridLayout } from "../native/workspace-grid";
import { directVector } from "./native-vector-display";
import {
  type RenderCanvas,
  type RenderContext,
  renderCanvas,
  type RenderImages,
} from "./canvas-surface";
import { activePage } from "../native/pages";
import type { EngineDocument, SceneNode } from "../scene/model";
import type { RasterEntry } from "./native-raster-cache";
import { NativeEffectsRaster } from "./native-effects-raster";
import { hasEffects } from "../native/effects";
import { clippingBase } from "../native/groups";
import {
  clippingFill,
  clippingPaintDocument,
  clippingPaintSource,
  clippingBorderSource,
} from "./native-clipping";
import { paintText } from "./native-text-layout";
import { paintVector } from "./native-vector-path";
import { cornerRadius } from "../native/corners";
import { nodeMatrix } from "../native/transforms";
import type { NativeFrame, NativeRenderer } from "./native-port";

import { hasPaint, shapeStyle, strokeRadius } from "../native/appearance";
import { paintShape } from "./native-shape-path";
export class NativeCanvasRenderer implements NativeRenderer {
  readonly kind = "canvas2d";
  private context: RenderContext;
  private effects = new NativeEffectsRaster();
  private layer = renderCanvas();
  constructor(
    private canvas: RenderCanvas,
    private images: RenderImages,
    private display = false,
    private displayRaster?: (
      document: EngineDocument,
      node: SceneNode,
      density: number,
    ) => RasterEntry,
  ) {
    const context = canvas.getContext("2d");
    if (!context) throw new Error("В браузере недоступна отрисовка холста.");
    this.context = context;
  }
  draw(
    { document, camera, width, height, dpr, workspaceGrid }: NativeFrame,
    transparent = false,
    onlyIds?: ReadonlySet<string>,
    raw = false,
    pageBackdrop = false,
  ) {
    const original = document;
    document = clippingPaintDocument(document, onlyIds);
    if (onlyIds && document !== original) {
      const selected = onlyIds;
      onlyIds = new Set([
        ...selected,
        ...activePage(document)
          .nodes.filter((n) => selected.has(clippingPaintSource(n)))
          .map((n) => n.id),
      ]);
    }
    if (this.canvas.width !== Math.round(width * dpr))
      this.canvas.width = Math.round(width * dpr);
    if (this.canvas.height !== Math.round(height * dpr))
      this.canvas.height = Math.round(height * dpr);
    const ctx = this.context,
      page = activePage(document);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    if (!transparent) {
      ctx.fillStyle = page.workspaceColor;
      ctx.fillRect(0, 0, width, height);
      const grid = workspaceGridLayout(
        camera,
        page.size,
        width,
        height,
        dpr,
        workspaceGrid,
        page.workspaceColor,
      );
      ctx.fillStyle = grid.color;
      for (const r of grid.rectangles)
        ctx.fillRect(r.x, r.y, r.width, r.height);
    }
    ctx.translate(camera.x, camera.y);
    ctx.scale(camera.zoom, camera.zoom);
    ctx.fillStyle = "#ffffff";
    if ((!transparent || pageBackdrop) && page.backgroundVisible)
      ctx.fillRect(0, 0, page.size.width, page.size.height);
    const sources = new Map(document.assets.map((a) => [a.id, a.source]));
    const visible = this.display
      ? new Set(
          visibleNodes({ document, camera, width, height, dpr }).map(
            (n) => n.id,
          ),
        )
      : undefined;
    for (const node of page.nodes) {
      if (visible && !visible.has(node.id)) continue;
      if (onlyIds && !onlyIds.has(node.id)) continue;
      if (node.appearance.visible === false || !hasPaint(node)) continue;
      if (
        this.displayRaster &&
        !directVector(node) &&
        (hasEffects(node) ||
          node.layer.clipToBelow ||
          node.kind === "text" ||
          node.kind === "path" ||
          isInteractive(node))
      ) {
        const entry = this.displayRaster(document, node, camera.zoom * dpr);
        ctx.save();
        ctx.globalAlpha = 1;
        if (entry.transform) ctx.transform(...entry.transform);
        ctx.globalCompositeOperation = (node.appearance.blendMode ??
          "source-over") as GlobalCompositeOperation;
        ctx.drawImage(
          entry.canvas,
          entry.box.x,
          entry.box.y,
          entry.box.width,
          entry.box.height,
        );
        ctx.restore();
        continue;
      }
      if (node.layer.clipToBelow) {
        const base = clippingBase(page.nodes, node);
        if (!base || base.appearance.visible === false) continue;
        const layer = documentCanvas(),
          mask = documentCanvas();
        const first = new NativeCanvasRenderer(layer, this.images),
          second = new NativeCanvasRenderer(mask, this.images);
        const common = { camera, width, height, dpr };
        first.draw(
          {
            ...common,
            document: {
              ...document,
              pages: [
                {
                  ...page,
                  nodes: [
                    { ...node, layer: { ...node.layer, clipToBelow: false } },
                  ],
                },
              ],
            },
          },
          true,
        );
        second.draw(
          {
            ...common,
            document: {
              ...document,
              pages: [{ ...page, nodes: [clippingFill(base)] }],
            },
          },
          true,
        );
        const output = layer.getContext("2d")!;
        output.setTransform(1, 0, 0, 1, 0, 0);
        output.globalCompositeOperation = "destination-in";
        output.drawImage(mask, 0, 0);
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalCompositeOperation = (node.appearance.blendMode ??
          "source-over") as GlobalCompositeOperation;
        ctx.drawImage(layer, 0, 0);
        ctx.restore();
        first.dispose();
        second.dispose();
        continue;
      }
      ctx.save();
      ctx.globalCompositeOperation = (node.appearance.blendMode ??
        "source-over") as GlobalCompositeOperation;
      ctx.globalAlpha = node.appearance.opacity ?? 1;
      if (!raw && hasEffects(node) && !directVector(node)) {
        const entry = this.effects.get(
          node,
          camera.zoom * dpr,
          (flat, surface, box, scale) => {
            const renderer = new NativeCanvasRenderer(surface, this.images);
            renderer.draw(
              {
                document: { ...document, pages: [{ ...page, nodes: [flat] }] },
                camera: { x: -box.x * scale, y: -box.y * scale, zoom: scale },
                width: surface.width,
                height: surface.height,
                dpr: 1,
              },
              true,
              undefined,
              true,
            );
            // The returned source belongs to the derived cache, so do not dispose it here.
          },
        );
        ctx.drawImage(
          entry.canvas,
          entry.box.x,
          entry.box.y,
          entry.box.width,
          entry.box.height,
        );
        ctx.restore();
        continue;
      }
      if (isInteractive(node) && !clippingBorderSource(node)) {
        if (this.layer.width !== this.canvas.width)
          this.layer.width = this.canvas.width;
        if (this.layer.height !== this.canvas.height)
          this.layer.height = this.canvas.height;
        const layer = this.layer.getContext("2d")!;
        layer.setTransform(1, 0, 0, 1, 0, 0);
        layer.clearRect(0, 0, this.layer.width, this.layer.height);
        layer.setTransform(
          dpr * camera.zoom,
          0,
          0,
          dpr * camera.zoom,
          dpr * camera.x,
          dpr * camera.y,
        );
        if (!node.interaction.interactiveImageEnabled) paintShape(layer, node);
        paintInteractiveLabel(layer, node, document.assets, this.images);
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.drawImage(this.layer, 0, 0);
      } else if (node.kind === "text" || node.kind === "path") {
        const paint = (target: RenderContext) => {
          const m = nodeMatrix(node),
            w = node.geometry.width!,
            h = node.geometry.height!;
          target.transform(m[0] / w, m[1] / w, m[2] / h, m[3] / h, m[4], m[5]);
          if (node.kind === "text") paintText(target, node);
          else paintVector(target, node);
        };
        if (ctx.globalAlpha < 1) {
          this.layer.width = this.canvas.width;
          this.layer.height = this.canvas.height;
          const surface = this.layer.getContext("2d")!;
          surface.setTransform(
            dpr * camera.zoom,
            0,
            0,
            dpr * camera.zoom,
            dpr * camera.x,
            dpr * camera.y,
          );
          paint(surface);
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.drawImage(this.layer, 0, 0);
        } else paint(ctx);
      } else if (node.kind !== "image") {
        if (
          ctx.globalAlpha < 1 &&
          strokeRadius(node) > 0 &&
          shapeStyle(node).fillEnabled
        ) {
          // Temporary viewport compositing only; no bitmap enters the document.
          if (this.layer.width !== this.canvas.width)
            this.layer.width = this.canvas.width;
          if (this.layer.height !== this.canvas.height)
            this.layer.height = this.canvas.height;
          const layer = this.layer.getContext("2d")!;
          layer.setTransform(1, 0, 0, 1, 0, 0);
          layer.clearRect(0, 0, this.layer.width, this.layer.height);
          layer.setTransform(
            dpr * camera.zoom,
            0,
            0,
            dpr * camera.zoom,
            dpr * camera.x,
            dpr * camera.y,
          );
          paintShape(layer, node);
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.drawImage(this.layer, 0, 0);
        } else paintShape(ctx, node);
      } else {
        const m = nodeMatrix(node),
          w = node.geometry.width!,
          h = node.geometry.height!;
        ctx.transform(m[0] / w, m[1] / w, m[2] / h, m[3] / h, m[4], m[5]);
        if (cornerRadius(node) > 0) {
          ctx.beginPath();
          ctx.roundRect(-w / 2, -h / 2, w, h, cornerRadius(node));
          ctx.clip();
        }
        const source = sources.get(node.media.image!) || "";
        const image = this.display
          ? this.images.display!(
              source,
              Math.hypot(m[0], m[1]) * camera.zoom * dpr,
              Math.hypot(m[2], m[3]) * camera.zoom * dpr,
            )
          : this.images.get(source);
        if (image) ctx.drawImage(image, -w / 2, -h / 2, w, h);
      }
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }
  dispose() {
    this.canvas.width = 0;
    this.canvas.height = 0;
    this.layer.width = this.layer.height = 0;
  }
}

function documentCanvas() {
  return renderCanvas();
}
import { isInteractive } from "../native/interactive";
import { paintInteractiveLabel } from "./native-interactive";
